/* ============================================================
   ILS — Cart gift logic (TASK-2407, rewritten by TASK-4845)
   Owns the client-side add/replace/remove of the free "dried mango" gift
   (SKU DF021F) as the cart's merchandise subtotal crosses configured
   thresholds. It is the SOLE cart-side writer of gift lines.

   Config source: GET https://admin.ilovesnacks.eu/api/cart-rewards/config
     → { gift_sku, thresholds: [{ threshold_amount, gift_weight, ... }, ...] }
   built by TASK-2406 (ils-translator/cart_rewards.js).

   ------------------------------------------------------------
   THE INVARIANT (the whole point of this file)
   ------------------------------------------------------------
   At any moment the persisted cart contains EXACTLY ONE gift line, of the
   weight earned by the current non-gift subtotal, at quantity 1 — or NO
   gift line at all when the subtotal is below the first threshold.

   ------------------------------------------------------------
   TASK-4845 — what changed and why (see REPORT-4842 for the forensics)
   ------------------------------------------------------------
   1. ONE REQUEST PER TRANSITION. Every tier change is now a single
      POST /{locale}/cart/update.js whose `updates` object expresses the
      whole final state: every unwanted gift line key → 0 AND the desired
      variant id → 1, together. The previous design was add-then-remove
      (two or three round-trips). That is what created the production
      duplicates: if the second request never landed — and on the checkout
      path it provably could not, because it was only constructed after the
      first response resolved, by which time the document was being
      discarded — the cart kept BOTH gifts forever.

      The premise the old design rested on ("update.js cannot add a variant
      that is not already in the cart") is false. It is contradicted by the
      current Cart API reference AND measured live on this shop before this
      code was written (recon/task4845/probe-1.json, phase A): a single
      `updates` object mixing a line-item KEY with a variant_id, plus
      `sections`, is accepted, and the response body is already the FINAL
      state. There is therefore no server state and no rendered frame in
      which both the old and the new gift exist.

   2. ALL GIFT LINES, NEVER THE FIRST ONE. findGiftLine() used to return
      only the first DF021F line, so a stranded older gift was invisible to
      the reconciler and could never be cleaned up — the duplicates were not
      merely likely, they were permanent. Now every gift line is enumerated,
      "no change needed" additionally requires that there is EXACTLY one,
      and any historical duplicate (500+125, 500+250+125, 1000+250+500, or
      two lines of the same variant) heals in ONE update.js request.

   3. SINGLE FLIGHT. The lock is taken synchronously at the top of
      reconcile(), before any await — it used to be set two awaits late, so
      two passes could both decide to add. A pass that arrives while one is
      running marks the run dirty and joins it; one fresh pass then runs
      immediately afterwards, on re-read data.

   4. ONE WRITER. reconcileBeforeNavigation() — a second, independent copy
      of the tier decision and its own add-then-remove mutation sequence —
      is gone. The checkout guard now calls the same reconciler, WAITS for
      it, and only then lets the navigation proceed.

   5. EVERY MUTATION SOURCE. Several live cart writers (the delivery
      upsell's "+ Add", the delivery selector, the pre-order popup) emit
      neither PUB_SUB cartUpdate nor DOM cart:update, so the reconciler
      never saw their adds (measured again in REPORT-4844's control B). A
      passive fetch observer — the same pass-through shape
      custom-add-toast.js already uses, the response is never touched and
      the caller's own promise is returned unchanged — schedules a
      reconcile after any successful cart write that is not one of ours.

   6. CURRENCY. cart.total_price is in the cart's PRESENTMENT currency
      minor units. It used to be divided by 100 and compared straight
      against the EUR ladder, so "70 DKK" was read as "€70" — 23 orders in
      28 days got the wrong (usually maximum) gift. The subtotal is now
      converted to EUR with Shopify's OWN published rate,
      window.Shopify.currency = { active, rate } (rate = presentment per
      one shop-currency unit). No rate is hardcoded and no third-party FX
      API is called. If that source is missing, or disagrees with
      cart.currency, or is not a positive finite number, the reconciler
      makes NO decision and leaves the cart exactly as it is.

   7. DELIVERY PROPERTIES. A gift line created after the delivery selector
      has already persisted its choice used to end up with 0 of the 6
      _delivery_* line-item properties (REPORT-4844 §6.2). After — and only
      after — the gift invariant is already correct, a best-effort
      change.js copies the properties from a line that has them onto the
      surviving gift line. It can never add a line, never changes quantity
      or variant, and its failure is not allowed to affect gift
      correctness.

   ------------------------------------------------------------
   Line references — STABLE KEY, never positional index (TASK-2665)
   ------------------------------------------------------------
   Every removal targets a Shopify line-item KEY (`variantId:propertiesHash`)
   read from a FRESH /cart.js immediately before the write — never a cached
   1-based positional `line` index. Positional indices were catastrophic
   here: /cart/add.js PREPENDS, so an index captured before a write points
   at the wrong row (gift stuck at 125 g — TASK-2663 Bug A; cart wiped —
   Bug B). An addition is keyed by variant_id, which update.js is
   documented to append when no line matches — and which we only ever use
   when we have just proven, from that same fresh read, that no gift line
   of that variant exists.

   Locale-rooted endpoints (TASK-2962): every cart call is prefixed with
   localeRoot(), exactly as custom-cart-drawer.js and custom-cart-page.js
   do. This file was the one that never got the prefix.

   Feature flag: window.ILS_CUSTOM_GIFT_LOGIC_ENABLED (default true) or
   URL param ?ilsGift=off / ?ilsGift=on (TASK-2672, reversible latch).

   ------------------------------------------------------------
   TASK-4850 — the checkout gate stops re-reading a cart it has already proven
   ------------------------------------------------------------
   REPORT-4848 measured this gate costing 166-220 ms on EVERY pre-checkout
   click in EVERY scenario, including the ones where it decides to do nothing,
   because runOnce() always issues a fresh GET /cart.js. The file already
   observes every cart mutation from four directions and already keeps
   lastResult; what was missing was a token saying "nothing has been written
   since a pass proved this cart correct".

   Added, behind the shared ILS_FASTPATH_DEFAULT kill switch (see the block
   below — the SAME resolver custom-delivery-selector.js uses, memoised on
   window.__ilsFastPath so the two files can never be half-enabled):

     * cartVersion — a monotonic counter bumped by every gift-relevant
       invalidator: a non-ours cart write seen by the fetch observer (bumped
       SYNCHRONOUSLY, before the response), PUB_SUB cartUpdate, DOM cart:update,
       pageshow[persisted], and visibilitychange -> visible (the cross-tab case).
     * a validity token armed ONLY by a plan whose reason is 'already-correct',
       and only when no mutation was observed while that pass was reading. A
       write result never arms it — 'update-failed', 'fx-unavailable',
       'no-config', 'cart-read-failed' and 'write-storm-guard' cannot.
     * the gate: when the token is armed for the CURRENT cartVersion, is fresh
       and no required write has failed, the TRUSTED click is let through
       untouched — no /cart.js, no preventDefault, no replay.

   NOT changed: the invariant, planGift(), the one-request atomic tier
   replacement of TASK-4845, the blocking behaviour of a DECIDED write that
   FAILED (res.blockNavigation still holds the shopper and shows the localized
   cart error), and stampGiftDeliveryProps() — which is still fully awaited.
   Deferring it (REPORT-4848's F3) is deliberately NOT part of this task.
   ============================================================ */
(function () {
  'use strict';

  const CONFIG_URL = 'https://admin.ilovesnacks.eu/api/cart-rewards/config';
  const DEFAULT_GIFT_SKU = 'DF021F';
  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  /* The LEGACY contract: `thresholds[]` is still five bare EUR amounts, and
     the shop's own currency is EUR (so Shopify.currency.rate is "1.0" on the
     EUR markets — asserted below rather than assumed). If the shop currency
     ever changes, this constant and the rate assertion must change with it.
     Still used verbatim for a currency with no approved ladder. */
  const THRESHOLD_CURRENCY = 'EUR';

  /* ===================================================================
     TASK-5954 — THE APPROVED LOCAL LADDER, AND WHY IT IS IN MINOR UNITS.

     Until now this file decided like so:

         nonGiftEur = (cart.total_price - giftLines) / 100 / rate
         grams      = highest EUR threshold <= nonGiftEur

     Two divisions by a live exchange rate, compared against a number that
     only existed in EUR. The owner-approved WORK-0132 matrix gives each of
     the six local-currency markets its OWN fixed amount per tier, so that
     comparison is now simply the wrong question: on SEK the "mango 500 g"
     rung is 1100 kr, which is EUR ~97.60, and the EUR rule granted only
     250 g there. REPORT-5951 measured it on all six.

     The approved amounts arrive as `reward_matrix` on
     GET /api/cart-rewards/config (ils-translator TASK-5954), in MINOR units.
     `cart.total_price` is ALREADY presentment minor units. So the decision
     becomes an integer comparison in ONE currency:

         nonGiftMinor = cart.total_price - sum(giftLine.line_price)
         grams        = highest approved minor <= nonGiftMinor

     No rate is read, nothing is divided, and nothing can drift between a
     printed number and a granted gift. ils-admin's post-order corrector
     applies the identical rule to the order's recorded presentment money,
     so cart and order cannot disagree either.

     EMBEDDED_LADDERS is the same matrix, compiled into this asset, used
     ONLY when the live config cannot be read or does not carry a matrix.
     It is a validated, currency-matched, owner-approved snapshot — the one
     fallback the rollout brief permits — and never a converted EUR ladder.
     It carries its own version so a drift against the backend is visible in
     `window.ILSGift.state()` rather than silent.
     =================================================================== */
  const APPROVED_VERSION = 'WORK-0136-contract-5a9a33ad';
  const MINOR_UNIT_FACTOR = 100;
  /* MAJOR units, exactly as the contract approves them. */
  const EMBEDDED_APPROVED_MAJOR = {
    EUR: { 60: 60,    80: 80,    100: 100,   150: 150,   200: 200 },
    PLN: { 60: 270,   80: 350,   100: 450,   150: 675,   200: 900 },
    CZK: { 60: 1500,  80: 2000,  100: 2500,  150: 3750,  200: 5000 },
    DKK: { 60: 450,   80: 600,   100: 750,   150: 1125,  200: 1500 },
    SEK: { 60: 650,   80: 900,   100: 1100,  150: 1650,  200: 2200 },
    HUF: { 60: 22000, 80: 29000, 100: 37000, 150: 55000, 200: 74000 },
    RON: { 60: 320,   80: 430,   100: 530,   150: 800,   200: 1060 },
  };
  /* The gift weight each mango tier grants, in grams. */
  const EMBEDDED_TIER_GRAMS = { 60: 125, 80: 250, 100: 500, 150: 750, 200: 1000 };

  /* Compile the embedded table into the same ladder shape the API's
     `reward_matrix` normalises to, so there is exactly one consumer shape. */
  function embeddedLadders() {
    const out = {};
    Object.keys(EMBEDDED_APPROVED_MAJOR).forEach(function (iso) {
      const row = EMBEDDED_APPROVED_MAJOR[iso];
      const tiers = Object.keys(row)
        .map(function (t) {
          return { tier_eur: Number(t), minor: Math.round(row[t] * MINOR_UNIT_FACTOR), grams: EMBEDDED_TIER_GRAMS[t] };
        })
        .filter(function (t) { return isFinite(t.minor) && t.minor > 0 && t.grams > 0; })
        .sort(function (a, b) { return a.minor - b.minor; });
      if (tiers.length) out[iso] = tiers;
    });
    return out;
  }

  /* Accept the API's matrix only if it is actually usable, else null. A
     half-parsed ladder is worse than none: null falls back to the embedded
     approved snapshot, whereas a ladder with a dropped rung would hand out
     the wrong gift confidently. */
  function parseApiLadders(matrix) {
    if (!matrix || typeof matrix !== 'object') return null;
    if (Number(matrix.minor_unit_factor) !== MINOR_UNIT_FACTOR) return null;
    const src = matrix.currencies;
    if (!src || typeof src !== 'object') return null;
    const out = {};
    Object.keys(src).forEach(function (iso) {
      const rows = src[iso] && Array.isArray(src[iso].tiers) ? src[iso].tiers : null;
      if (!rows) return;
      const all = rows
        .map(function (t) {
          return {
            tier_eur: Number(t && t.tier_eur),
            minor: Number(t && t.minor),
            grams: weightToGrams(t && t.gift_weight),
          };
        })
        .filter(function (t) { return isFinite(t.minor) && t.minor > 0; })
        .sort(function (a, b) { return a.tier_eur - b.tier_eur; });
      /* STRICTLY ascending across the WHOLE published ladder, not only the
         rungs that happen to carry a gift. A payload whose tier 100 sits at
         or below its tier 90 is corrupt however the gift rungs read, and a
         corrupt payload must send us to the embedded approved snapshot
         rather than be partly believed. */
      let ok = all.length > 0;
      for (let i = 1; i < all.length && ok; i++) if (all[i].minor <= all[i - 1].minor) ok = false;
      /* The decision only ever walks the gift rungs, so keep just those. */
      const tiers = all
        .filter(function (t) { return t.grams > 0; })
        .sort(function (a, b) { return a.minor - b.minor; });
      if (ok && tiers.length) out[String(iso).toUpperCase()] = tiers;
    });
    return Object.keys(out).length ? out : null;
  }

  /* =====================================================================
     TASK-4850 — the shared fast-path kill switch and the gift validity token.
     ILS_FASTPATH_DEFAULT is the single toggle; the resolved value is memoised
     on window.__ilsFastPath, which custom-delivery-selector.js resolves through
     the identical function, so the two files always agree at runtime however
     they are deployed. OFF restores the exact pre-TASK-4850 behaviour: the gate
     always reconciles, nothing below is consulted and no listener added by this
     task is registered.
     ===================================================================== */
  const ILS_FASTPATH_DEFAULT = true;    /* <-- THE kill switch. false = legacy. */

  function resolveFastPath() {
    try {
      const w = window;
      if (w.__ilsFastPath && typeof w.__ilsFastPath.enabled === 'boolean') return w.__ilsFastPath;
      let on = ILS_FASTPATH_DEFAULT;
      if (w.ILS_FASTPATH_ENABLED === false) on = false;
      else if (w.ILS_FASTPATH_ENABLED === true) on = true;
      const qs = (w.location && w.location.search) || '';
      if (/[?&]ilsFast=off\b/.test(qs)) on = false;
      else if (/[?&]ilsFast=on\b/.test(qs)) on = true;
      w.__ilsFastPath = { enabled: !!on, owner: 'TASK-4850' };
      return w.__ilsFastPath;
    } catch (e) { return { enabled: false, owner: 'TASK-4850' }; }
  }
  const FASTPATH = resolveFastPath().enabled;

  /* =====================================================================
     TASK-4945 — the gift half of ALWAYS-IMMEDIATE NEXT.
     The resolver is byte-identical to custom-delivery-selector.js's and memoises
     on the same window.__ilsInstantNext, so the two files always agree at
     runtime however they are deployed. OFF restores TASK-4854 exactly: the
     delivery page's Next button is back in the capture-phase blocking gate and
     the 8-refresh-per-view budget is back.

     TWO changes, both of them REPORT-4935 findings:

     1. [data-ils-next] LEAVES the blocking gate. The gate's job is to make a
        pre-checkout navigation start from a reconciled gift state, and the
        navigation that matters is cart/drawer -> delivery page: those gates STAY,
        so the delivery page is always entered gift-correct. Holding the delivery
        page's own Next as well bought nothing and cost a blocking /cart.js
        (166-220 ms, REPORT-4848) on every click whose token had lapsed. The gift
        invariant itself does not weaken: every genuine cart mutation still wakes
        the reconciler synchronously, TASK-4845's atomic single-request tier
        replacement is untouched, and gift_correction.js runs in apply mode on
        every paid order as the durable net.

     2. THE 8-REFRESH-PER-VIEW BUDGET GOES. REPORT-4935 sect. 1.1 measured it as the
        dominant cause of the reported regression: after roughly two minutes of
        dwell the budget is spent, the 30 s token lapses PERMANENTLY for that page
        view, and from then on every click paid both the gate's blocking read and
        the selector's full 1 + N pass. Bounding a background read by a per-view
        LIFETIME cap is the wrong bound; the right one is frequency. The refresh
        is therefore gated on tokenNeedsRefresh() plus a minimum gap, with no
        lifetime cap — at a 20 s gap that is at most 3 reads a minute, and only
        while the token actually needs one, the document is visible and a real
        checkout CTA is on the page. No polling storm, and no silent cliff.
     ===================================================================== */
  const ILS_INSTANT_NEXT_DEFAULT = true;   /* <-- THE kill switch. false = TASK-4854. */

  function resolveInstantNext() {
    try {
      const w = window;
      if (w.__ilsInstantNext && typeof w.__ilsInstantNext.enabled === 'boolean') return w.__ilsInstantNext;
      let on = ILS_INSTANT_NEXT_DEFAULT;
      if (w.ILS_INSTANT_NEXT_ENABLED === false) on = false;
      else if (w.ILS_INSTANT_NEXT_ENABLED === true) on = true;
      const qs = (w.location && w.location.search) || '';
      if (/[?&]ilsNext=off\b/.test(qs)) on = false;
      else if (/[?&]ilsNext=on\b/.test(qs)) on = true;
      w.__ilsInstantNext = { enabled: !!on, owner: 'TASK-4945' };
      return w.__ilsInstantNext;
    } catch (e) { return { enabled: false, owner: 'TASK-4945' }; }
  }
  const INSTANT_NEXT = FASTPATH && resolveInstantNext().enabled;

  /* TTL, and why it is 30 s rather than the 5-10 s first sketched. The token's
     freshness window guards exactly ONE thing: a cart change made OUTSIDE this
     document. Everything made inside it is already caught synchronously by the
     fetch observer, PUB_SUB cartUpdate and DOM cart:update; a change made in
     another tab is caught by visibilitychange -> visible; a change made in a
     side-by-side WINDOW (where this document may never go hidden) is caught by
     the blur -> focus pair below. A 10 s TTL was measured against the real page
     and rejected: the shopper's dwell on the delivery page is far longer than
     that, so the token expired before nearly every click and put the 166-220 ms
     /cart.js straight back on the critical path — the very cost this change
     exists to remove (measured: 356-409 ms, recon/task4850/verify.dev.on.json,
     D1 under the first build). The residual exposure a longer TTL accepts is a
     SERVER-side cart change while this tab stays both visible and focused; the
     file's existing safety nets (the init pass, the pageshow pass and the
     post-order corrector) are unchanged and still cover it. */
  const GIFT_TOKEN_TTL_MS = 30000;
  /* Rather than let the token lapse and pay for it on the click, refresh it
     PRE-EMPTIVELY while a checkout is plausibly imminent — but never as a free
     polling loop. Every refresh must pass all of: the token is within
     PREEMPT_MS of expiry (or already invalid), no pass is running or pending,
     the document is visible, a real checkout CTA is present (the delivery
     selector's own button or the cart page's — the drawer's merely redirects to
     the delivery page, which gates properly, so it is deliberately excluded),
     at least MIN_GAP since the last refresh, and fewer than MAX_PER_VIEW
     refreshes so far in this page view. At these numbers a steady state is one
     /cart.js every ~22 s and at most 8 in a page view, after which the token
     simply lapses and the gate goes back to reading — a graceful degradation,
     never an unbounded loop. */
  const GIFT_REFRESH_PREEMPT_MS = 8000;
  /* TASK-4945 — frequency, not lifetime. See the block at ILS_INSTANT_NEXT_DEFAULT:
     the per-view cap is the measured root cause of the long-dwell regression, so
     under instant-next there is no cap and the gap is widened to 20 s instead
     (at most 3 reads a minute, and only when the token really needs one). */
  const GIFT_REFRESH_MIN_GAP_MS = INSTANT_NEXT ? 20000 : 15000;
  const GIFT_REFRESH_MAX_PER_VIEW = 8;
  const GIFT_REFRESH_CAPPED = !INSTANT_NEXT;
  const GIFT_REFRESH_TICK_MS = 4000;
  const GIFT_REFRESH_INTENT_SEL = '[data-ils-next], #CartPage-Checkout';

  let cartVersion = 1;              /* monotonic; every invalidator bumps it */
  let validated = null;             /* { version, at, reason } — the token */
  let requiredWriteFailed = false;  /* a DECIDED write failed: never arm */
  let lastInvalidation = null;
  const fastStats = { bumps: 0, arms: 0, passThroughs: 0, gateReconciles: 0, refreshes: 0 };

  function bumpCartVersion(why) {
    cartVersion++;
    lastInvalidation = why || null;
    fastStats.bumps++;
    validated = null;
  }

  /* Every condition the task requires, and nothing else may shortcut it. */
  function giftFastReady() {
    if (!FASTPATH) return false;
    if (requiredWriteFailed) return false;
    if (!validated) return false;
    if (validated.version !== cartVersion) return false;
    if (running) return false;            /* a pass is in flight */
    if (debounceTimer) return false;      /* a pass is pending */
    if ((Date.now() - validated.at) > GIFT_TOKEN_TTL_MS) return false;
    return true;
  }

  /* CORE CHANGE D — recognise custom-delivery-selector.js's own writes. The
     marker alone is not trusted: the body must really be attributes-only (all
     keys delivery_*) or line-properties-only (all keys _delivery_*), so a
     mismarked write that COULD move a quantity, variant or line still wakes the
     reconciler. A genuine add / remove / quantity / variant write — including
     the delivery upsell's "+ Add" — carries no marker and is never ignored. */
  function isDeliveryMetaWrite(init) {
    if (!init || init.__ilsDeliveryMeta !== true) return false;
    try {
      const b = JSON.parse(String(init.body || ''));
      if (!b || typeof b !== 'object') return false;
      const keys = Object.keys(b);
      if (keys.length === 1 && keys[0] === 'attributes' && b.attributes && typeof b.attributes === 'object') {
        return Object.keys(b.attributes).every(function (k) { return /^delivery_/.test(k); });
      }
      const allowed = { id: 1, quantity: 1, properties: 1 };
      if (keys.length && keys.every(function (k) { return allowed[k] === 1; }) &&
          b.properties && typeof b.properties === 'object' &&
          Object.keys(b.properties).length) {
        return Object.keys(b.properties).every(function (k) { return /^_delivery_/.test(k); });
      }
    } catch (e) {}
    return false;
  }

  /* Sticky-disable latch key. TASK-2672 bumped it from the legacy
     'ils.giftLogic.off' to '…off.v2' so sessions stuck under the old key
     (owner's bug) are ignored and auto-heal on their next page load. */
  const OFF_KEY = 'ils.giftLogic.off.v2';
  const LEGACY_OFF_KEY = 'ils.giftLogic.off';

  /* Variant IDs for the "GIFT (do not add manually) - Dried mango without
     sugar - Vietnam" product (gid://shopify/Product/9658391396693). All 5
     variants share SKU DF021F, price €0.00, product status UNLISTED (not
     browseable, only reachable by direct add), inventoryPolicy CONTINUE (an
     add can never fail on stock). Re-verified live 2026-09-25 (TASK-4845).
     Keyed by grams — matches the API config's `gift_weight` values. */
  const GIFT_VARIANT_BY_GRAMS = {
    125:  49820818047317,
    250:  49820818080085,
    500:  49820818112853,
    750:  52303207137621,
    1000: 52303207170389,
  };
  /* Same set as a lookup, used as a SECOND way to recognise a gift line.
     Enumeration must not depend on cart.js reporting `sku`: a gift line we
     failed to recognise is a gift line we can never clean up, which is
     exactly the bug this task exists to remove. These ids belong to no
     other product, so widening the match cannot touch a shopper's line. */
  const GIFT_VARIANT_IDS = {};
  Object.keys(GIFT_VARIANT_BY_GRAMS).forEach(function (g) {
    GIFT_VARIANT_IDS[String(GIFT_VARIANT_BY_GRAMS[g])] = true;
  });

  /* ---------------------------------------------------------------
     Locale-rooted endpoints (TASK-2962). Same implementation as
     custom-cart-drawer.js: '' or '/ru-lv', never a trailing slash.
     --------------------------------------------------------------- */
  const ILS_KNOWN_LOCALE_PREFIXES = [
    '/en-eu', '/ru-eu',
    '/de-de', '/ru-de',
    '/lv-lv', '/ru-lv',
    '/et-ee', '/ru-ee',
    '/lt-lt', '/ru-lt'
  ];
  function localeRoot() {
    try {
      const r = window.Shopify && Shopify.routes && Shopify.routes.root;
      if (typeof r === 'string' && r.length > 0) {
        const trimmed = r.replace(/\/+$/, '');
        return trimmed === '' ? '' : trimmed;
      }
    } catch (e) {}
    const path = (window.location && location.pathname) || '';
    for (let i = 0; i < ILS_KNOWN_LOCALE_PREFIXES.length; i++) {
      const pfx = ILS_KNOWN_LOCALE_PREFIXES[i];
      if (path === pfx || path.indexOf(pfx + '/') === 0) return pfx;
    }
    return '';
  }

  function killed() {
    try {
      /* Drop the legacy latch key so a pre-TASK-2672 session that got stuck
         under it (?ilsGift=off with no way back) self-heals on this load. */
      try { sessionStorage.removeItem(LEGACY_OFF_KEY); } catch (e) {}

      /* Explicit programmatic kill always wins. */
      if (window.ILS_CUSTOM_GIFT_LOGIC_ENABLED === false) return true;

      const qs = (window.location && window.location.search) || '';
      /* ?ilsGift=on is the REVERSE of ?ilsGift=off: clear the sticky latch and
         re-enable WITHOUT closing the tab. Checked first so it wins over a
         stored latch and over a stray =off elsewhere in the query string. */
      if (/[?&]ilsGift=on\b/.test(qs)) {
        try { sessionStorage.removeItem(OFF_KEY); } catch (e) {}
        return false;
      }
      if (/[?&]ilsGift=off\b/.test(qs)) {
        try { sessionStorage.setItem(OFF_KEY, '1'); } catch (e) {}
      }
      if (sessionStorage.getItem(OFF_KEY) === '1') return true;
    } catch (e) {}
    return false;
  }

  function weightToGrams(raw) {
    if (raw == null) return 0;
    const s = String(raw).trim();
    const m = s.match(/(\d+(?:[.,]\d+)?)/);
    if (!m) return 0;
    let n = parseFloat(m[1].replace(',', '.'));
    if (!isFinite(n) || n <= 0) return 0;
    if (/kg/i.test(s)) n *= 1000;
    return Math.round(n);
  }

  let cfgCache = null;      /* { gift_sku, thresholds: [{amount, grams}] } */
  let cfgAttempted = false;

  async function fetchConfig() {
    try {
      const r = await fetch(CONFIG_URL, {
        headers: { Accept: 'application/json' },
        credentials: 'omit',
      });
      if (!r.ok) return null;
      const data = await r.json();
      if (!data || !Array.isArray(data.thresholds)) return null;
      /* TASK-5954 — prefer the backend's published matrix, fall back to the
         embedded approved snapshot. `ladderSource` is surfaced in state()
         so "which table granted this gift" is never a guess. */
      const apiLadders = parseApiLadders(data.reward_matrix);
      const ladders = apiLadders || embeddedLadders();
      const ladderSource = apiLadders ? 'api' : 'embedded';
      const ladderVersion = (data.reward_matrix && data.reward_matrix.version_id)
        || data.reward_matrix_version || (apiLadders ? null : APPROVED_VERSION);
      const thresholds = data.thresholds
        .map(function (t) {
          return {
            amount: Number(t && t.threshold_amount),
            grams:  weightToGrams(t && t.gift_weight),
          };
        })
        .filter(function (t) { return isFinite(t.amount) && t.amount > 0 && t.grams > 0; })
        .sort(function (a, b) { return a.amount - b.amount; });
      if (!thresholds.length) return null;
      return {
        gift_sku: String(data.gift_sku || DEFAULT_GIFT_SKU),
        thresholds: thresholds,
        ladders: ladders,
        ladderSource: ladderSource,
        ladderVersion: ladderVersion,
      };
    } catch (e) { return null; }
  }

  async function ensureConfig() {
    if (cfgCache) return cfgCache;
    if (cfgAttempted) return cfgCache;
    cfgAttempted = true;
    cfgCache = await fetchConfig();
    /* NOTE — one intentional retry after 5s if the first attempt failed.
       If both fail we return null forever this session and the reconciler
       becomes a no-op (fail-safe: better to leave the cart alone than to
       trash it because our config service blipped). */
    if (!cfgCache) {
      setTimeout(async function () {
        cfgCache = await fetchConfig();
      }, 5000);
    }
    return cfgCache;
  }

  /* Given the non-gift subtotal in EUR, return the grams weight earned.
     0 = no gift. THE LEGACY RULE — reached only for a currency with no
     approved ladder (see targetGramsForMinor). */
  function targetGramsForTotal(cfg, totalEur) {
    let chosen = 0;
    for (let i = 0; i < cfg.thresholds.length; i++) {
      if (totalEur >= cfg.thresholds[i].amount) chosen = cfg.thresholds[i].grams;
    }
    return chosen;
  }

  /* TASK-5954 — THE RULE. Given the non-gift subtotal in PRESENTMENT MINOR
     units and the cart's own currency, return the grams earned per the
     approved ladder, or null meaning "no ladder for this currency — make NO
     decision". `>=` exactly, the same boundary the bars print and the
     post-order corrector applies. */
  function targetGramsForMinor(cfg, minor, iso) {
    const code = String(iso || '').toUpperCase();
    const ladder = cfg && cfg.ladders ? cfg.ladders[code] : null;
    if (!ladder || !ladder.length) return null;
    const n = Number(minor);
    if (!isFinite(n) || n < 0) return null;
    let chosen = 0;
    for (let i = 0; i < ladder.length; i++) if (n >= ladder[i].minor) chosen = ladder[i].grams;
    return chosen;
  }

  /* ---------------------------------------------------------------
     CORE FIX 6 — presentment currency → EUR, from Shopify's own data.

     cart.currency is the cart's presentment currency.
     window.Shopify.currency = { active, rate } is emitted by Shopify in
     content_for_header on every storefront page; `rate` is how many units
     of `active` one unit of the SHOP currency buys (measured live:
     DKK 7.6258158, PLN 4.470864, SEK 11.519472, CZK 24.895548,
     HUF 373.65966, EUR 1.0 — recon/task4845/probe-1.json phase C).

     Returns the divisor to take presentment major units to EUR, or null
     meaning "the conversion source is unavailable or inconsistent — make
     NO decision and leave the cart alone".
     --------------------------------------------------------------- */
  function eurRateFor(cart) {
    const cartCur = String((cart && cart.currency) || '').toUpperCase();
    if (!cartCur) return null;

    let active = '', rate = NaN;
    try {
      const c = window.Shopify && window.Shopify.currency;
      if (c) { active = String(c.active || '').toUpperCase(); rate = Number(c.rate); }
    } catch (e) {}

    if (cartCur === THRESHOLD_CURRENCY) {
      /* The EUR path is unchanged by design: divisor 1, no dependency on
         the global at all. We still assert the global when it is present
         and claims EUR — a rate other than 1 there would mean the SHOP
         currency is no longer EUR, i.e. the ladder's own unit moved under
         us, and guessing would be exactly what this fix forbids. */
      if (active === THRESHOLD_CURRENCY && isFinite(rate) && Math.abs(rate - 1) > 0.0001) return null;
      return 1;
    }

    /* Non-EUR presentment: the global must exist, agree with the cart, and
       carry a usable rate. Anything else → refuse to decide. */
    if (active !== cartCur) return null;
    if (!isFinite(rate) || rate <= 0) return null;
    return rate;
  }

  /* ---------------------------------------------------------------
     CORE FIX 2 — enumerate EVERY gift line, never just the first.
     --------------------------------------------------------------- */
  function giftLinesOf(cart, giftSku) {
    const items = (cart && cart.items) || [];
    const wanted = String(giftSku || DEFAULT_GIFT_SKU).toUpperCase();
    const out = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (!it) continue;
      const bySku = String(it.sku || '').toUpperCase() === wanted;
      const byId = GIFT_VARIANT_IDS[String(it.variant_id)] === true;
      if (!bySku && !byId) continue;
      out.push({
        key: it.key,
        variant_id: it.variant_id,
        quantity: it.quantity || 0,
        line_price: it.line_price | 0,
        item: it,
      });
    }
    return out;
  }

  /* ---------------------------------------------------------------
     The decision. Pure: takes a fresh cart, returns the ONE updates
     object that produces the final invariant — or null for a genuine
     no-op. Never a sequence, never a second request.
     --------------------------------------------------------------- */
  function planGift(cfg, cart) {
    const gifts = giftLinesOf(cart, cfg.gift_sku);
    const cartCur = String((cart && cart.currency) || '').toUpperCase();

    const totalCents = cart.total_price | 0;
    let giftCents = 0;
    for (let i = 0; i < gifts.length; i++) giftCents += gifts[i].line_price;
    /* Subtract every gift line's own value — the old code subtracted only
       the first, so a duplicate under-subtracted. No numeric effect while
       the gift is €0.00, but the ladder must not silently depend on that. */
    const nonGiftMinor = Math.max(0, totalCents - giftCents);

    /* ---------------------------------------------------------------
       TASK-5954 — THE DECISION, in presentment minor units.

       Both operands are integers in the cart's own currency, so there is
       no rate to read, nothing to divide and nothing to drift. This is the
       path every market takes now, EUR included (the approved EUR column
       IS the EUR ladder, so EUR carts behave exactly as before).
       --------------------------------------------------------------- */
    let targetGrams = targetGramsForMinor(cfg, nonGiftMinor, cartCur);
    let rate = null;
    let nonGiftEur = null;
    let basis = 'presentment-minor';

    if (targetGrams === null) {
      /* No approved ladder for this currency. Fall back to the legacy EUR
         rule, which needs the rate — and ONLY for a currency the approved
         matrix does not cover, so a fixed-local market can never be
         decided by a converted EUR comparison again. */
      basis = 'legacy-eur';
      rate = eurRateFor(cart);
      if (rate === null) {
        /* Fail safe (CORE FIX 6): no rate → no decision → no write. This is
           NOT an error: we simply do not know the EUR subtotal, so the cart
           is left exactly as the shopper left it. */
        return { ok: true, updates: null, reason: 'fx-unavailable',
                 currency: (cart && cart.currency) || null, gifts: gifts };
      }
      nonGiftEur = Math.max(0, nonGiftMinor / 100 / rate);
      targetGrams = targetGramsForTotal(cfg, nonGiftEur);
    }

    let desiredVariantId = 0;
    if (targetGrams > 0) {
      desiredVariantId = GIFT_VARIANT_BY_GRAMS[targetGrams] || 0;
      if (!desiredVariantId) {
        /* The config earned a weight this theme has no variant for (e.g.
           someone added a 300 g tier upstream). Removing the shopper's
           gift because we cannot name its replacement would be a
           regression, so: no decision, no write. */
        return { ok: true, updates: null, reason: 'unmapped-weight',
                 targetGrams: targetGrams, nonGiftEur: nonGiftEur, gifts: gifts };
      }
    }

    const base = {
      ok: true, currency: cart.currency, rate: rate, nonGiftEur: nonGiftEur,
      /* TASK-5954 — the integers the decision was actually made on, and
         which table made it. `nonGiftEur`/`rate` stay in the shape every
         existing reader expects, and are null on the minor-unit path
         precisely because no rate was consulted. */
      nonGiftMinor: nonGiftMinor, basis: basis,
      ladderSource: cfg.ladderSource || null, ladderVersion: cfg.ladderVersion || null,
      targetGrams: targetGrams, desiredVariantId: desiredVariantId, gifts: gifts,
    };

    /* Below the first threshold: every gift line goes to 0, in one request. */
    if (!desiredVariantId) {
      if (!gifts.length) return Object.assign(base, { updates: null, reason: 'already-correct' });
      const updates = {};
      for (let i = 0; i < gifts.length; i++) updates[gifts[i].key] = 0;
      return Object.assign(base, { updates: updates, reason: 'remove-all' });
    }

    /* A weight is earned. Pick ONE surviving line of the desired variant if
       the cart already has one — keeping a line is cheaper than replacing
       it and preserves whatever properties it already carries. */
    let keeper = null;
    for (let i = 0; i < gifts.length; i++) {
      if (gifts[i].variant_id === desiredVariantId) { keeper = gifts[i]; break; }
    }

    /* The ONLY legal no-op: exactly one gift line, the right variant,
       quantity exactly 1. Anything else is healed. */
    if (keeper && gifts.length === 1 && keeper.quantity === 1) {
      return Object.assign(base, { updates: null, reason: 'already-correct' });
    }

    const updates = {};
    for (let i = 0; i < gifts.length; i++) {
      if (keeper && gifts[i].key === keeper.key) continue;
      updates[gifts[i].key] = 0;          /* every extra / wrong-weight line */
    }
    if (keeper) {
      if (keeper.quantity !== 1) updates[keeper.key] = 1;
      return Object.assign(base, { updates: updates, reason: 'heal', addedVariantId: 0 });
    }
    /* No line of the desired variant exists — add it BY VARIANT ID in the
       same object that zeroes the others. Verified accepted on this shop
       (recon/task4845/probe-1.json phase A). Safe against update.js's
       "a variant_id that matches multiple lines updates only the first"
       caveat precisely because we only take this branch when the fresh
       read proved there is no line of that variant at all. */
    updates[String(desiredVariantId)] = 1;
    return Object.assign(base, { updates: updates, reason: 'replace', addedVariantId: desiredVariantId });
  }

  /* ---------------------------------------------------------------
     Transport. `__ilsGift` marks our own writes so the fetch observer
     below does not schedule a reconcile in response to them (fetch
     ignores unknown init members).
     --------------------------------------------------------------- */
  function postJson(url, body, opts) {
    const init = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      __ilsGift: true,
    };
    if (opts && opts.keepalive) init.keepalive = true;
    return fetch(url, init).then(function (r) {
      return r.json().then(
        function (d) { return { ok: r.ok, status: r.status, data: d }; },
        function () { return { ok: r.ok, status: r.status, data: null }; }
      );
    }, function (e) {
      return { ok: false, status: 0, data: null, error: String((e && e.message) || e) };
    });
  }

  async function readCart() {
    try {
      const r = await fetch(localeRoot() + '/cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        __ilsGift: true,
      });
      if (!r.ok) return null;
      const c = await r.json();
      return (c && Array.isArray(c.items)) ? c : null;
    } catch (e) { return null; }
  }

  /* ---------------------------------------------------------------
     CORE FIX 7 — delivery properties on the surviving gift line.
     Runs ONLY after the gift invariant is already correct, and only ever
     touches the single surviving gift line. change.js by key can never
     create a line, and quantity is re-sent unchanged, so this cannot
     affect the invariant it runs behind. Copies the exact property object
     off a line that already has them (never reconstructs it), exactly as
     custom-delivery-upsell.js:stampDeliveryProps() does.
     --------------------------------------------------------------- */
  const DELIVERY_PROP_RE = /^_delivery_/;

  function deliveryPropsOf(item) {
    const p = item && item.properties;
    if (!p) return null;
    let found = null;
    for (const k in p) {
      if (!Object.prototype.hasOwnProperty.call(p, k)) continue;
      if (!DELIVERY_PROP_RE.test(k)) continue;
      if (!found) found = {};
      found[k] = p[k];
    }
    return found;
  }

  async function stampGiftDeliveryProps() {
    const cart = await readCart();
    if (!cart) return { stamped: false, reason: 'cart-read-failed' };
    const gifts = giftLinesOf(cart, (cfgCache && cfgCache.gift_sku) || DEFAULT_GIFT_SKU);
    /* Never act unless the invariant we just established still holds. */
    if (gifts.length !== 1 || gifts[0].quantity !== 1) return { stamped: false, reason: 'not-single-gift' };
    const gift = gifts[0];
    if (deliveryPropsOf(gift.item)) return { stamped: false, reason: 'already-stamped' };

    let props = null;
    const items = cart.items || [];
    for (let i = 0; i < items.length && !props; i++) {
      if (items[i] && items[i].key === gift.key) continue;
      props = deliveryPropsOf(items[i]);
    }
    if (!props) return { stamped: false, reason: 'nothing-persisted' };

    const out = await postJson(localeRoot() + '/cart/change.js', {
      id: String(gift.key),
      quantity: gift.quantity,     /* re-sent unchanged: only properties move */
      properties: props,
      sections: SECTIONS,
    });
    if (!out.ok) return { stamped: false, reason: 'change-failed', status: out.status };
    applyFinalSection(out.data);

    /* Re-read and verify the SURVIVING gift line really carries them, and
       that stamping did not disturb the invariant. */
    const after = await readCart();
    if (!after) return { stamped: true, verified: false, reason: 'verify-read-failed' };
    const g2 = giftLinesOf(after, (cfgCache && cfgCache.gift_sku) || DEFAULT_GIFT_SKU);
    const ok = g2.length === 1 && g2[0].variant_id === gift.variant_id &&
               g2[0].quantity === 1 && !!deliveryPropsOf(g2[0].item);
    return { stamped: true, verified: ok, props: Object.keys(props).length };
  }

  /* ---------------------------------------------------------------
     One reconciliation pass. Exactly one cart WRITE at most (plus the
     best-effort property stamp, which is not a gift write).
     --------------------------------------------------------------- */
  /* A condition we deliberately did not act on is not an error, but it
     must not be silent either. Reported once per distinct reason per page,
     to the console and as a DOM event for any monitoring that wants it. */
  const reported = {};
  function reportCondition(reason, detail) {
    if (reported[reason]) return;
    reported[reason] = true;
    try { console.warn('[ils-gift] no decision made: ' + reason, detail || ''); } catch (e) {}
    try {
      document.dispatchEvent(new CustomEvent('ils:gift-condition', { detail: { reason: reason, info: detail || null } }));
    } catch (e) {}
  }

  /* Write-storm breaker. Every write we issue should make the very next
     plan a no-op; if the identical plan comes back a fourth time in a row
     something upstream is refusing our update and retrying forever would
     hammer the shop into the 429 bot-protection window. */
  let lastWriteSig = '';
  let repeatWrites = 0;
  const MAX_REPEAT_WRITES = 3;

  async function runOnce() {
    /* TASK-4850: the version as it was BEFORE this pass read the cart. A
       mutation observed while we were reading means what we proved is already
       out of date, so the token must not be armed for it. */
    const startVersion = cartVersion;
    const cfg = await ensureConfig();
    if (!cfg) { reportCondition('no-config'); return { ok: true, changed: false, reason: 'no-config', writes: 0 }; }

    const cart = await readCart();
    if (!cart) return { ok: true, changed: false, reason: 'cart-read-failed', writes: 0 };

    const plan = planGift(cfg, cart);
    if (!plan.updates) {
      if (plan.reason === 'fx-unavailable') reportCondition('fx-unavailable', plan.currency);
      if (plan.reason === 'unmapped-weight') reportCondition('unmapped-weight', plan.targetGrams);
      /* The cart is provably correct — whatever we wrote last took effect,
         so the storm breaker starts from a clean slate. */
      if (plan.reason === 'already-correct') {
        lastWriteSig = ''; repeatWrites = 0;
        /* TASK-4850 — the ONLY thing that may arm the validity token: a plan
           that PROVED, from a cart read that nothing mutated across, that the
           invariant already holds. A failed required write is cleared here too:
           the cart is now provably correct, so the failure is spent (this is
           what makes "retry after recovery succeeds exactly once" work). */
        requiredWriteFailed = false;
        if (FASTPATH && cartVersion === startVersion) {
          validated = { version: startVersion, at: Date.now(), reason: 'already-correct' };
          fastStats.arms++;
        }
      }
      return { ok: true, changed: false, reason: plan.reason, plan: plan, writes: 0 };
    }

    const sig = JSON.stringify(plan.updates);
    if (sig === lastWriteSig) {
      if (repeatWrites >= MAX_REPEAT_WRITES) {
        reportCondition('write-not-taking-effect', sig);
        return { ok: true, changed: false, reason: 'write-storm-guard', plan: plan, writes: 0 };
      }
      repeatWrites++;
    } else {
      lastWriteSig = sig;
      repeatWrites = 1;
    }

    const out = await postJson(localeRoot() + '/cart/update.js', {
      updates: plan.updates,
      sections: SECTIONS,
    });
    if (!out.ok) {
      /* A write we DECIDED on failed. This is the only condition that may
         hold a checkout navigation: we know the cart is wrong. */
      /* TASK-4850: and the fast gate must stay shut until a later pass proves
         the cart correct again. */
      requiredWriteFailed = true;
      validated = null;
      return { ok: false, changed: false, reason: 'update-failed', status: out.status,
               blockNavigation: true, plan: plan, writes: 1 };
    }
    applyFinalSection(out.data);

    /* TASK-4850: we just changed the cart, so any token is void. Our own write
       carries __ilsGift, so nothing schedules a pass behind it — schedule ONE
       verification pass so a tier crossing ends in a PROVEN-correct state and
       the token can arm for the NEXT click. A write result is never itself a
       proof. On the checkout path this timer never fires (the document is
       discarded), so it costs nothing there. */
    validated = null;
    if (FASTPATH) scheduleReconcile(700);

    let stamp = null;
    if (plan.addedVariantId) {
      /* Best-effort only — never allowed to change `ok`. */
      try { stamp = await stampGiftDeliveryProps(); } catch (e) { stamp = { stamped: false, reason: 'exception' }; }
    }
    return { ok: true, changed: true, reason: plan.reason, plan: plan, writes: 1, stamp: stamp };
  }

  /* ---------------------------------------------------------------
     CORE FIX 3 — single flight. The lock is taken SYNCHRONOUSLY here,
     before runOnce() is even called, so two callers in the same tick
     cannot both pass it. A caller that arrives mid-flight marks the run
     dirty and receives the SAME promise — which resolves only after the
     extra, fully fresh pass that the dirty flag forces.
     --------------------------------------------------------------- */
  let running = null;
  let dirty = false;
  let lastResult = null;

  function reconcile() {
    if (killed()) return Promise.resolve({ ok: true, changed: false, reason: 'killed' });
    if (running) { dirty = true; return running; }

    let settle;
    running = new Promise(function (res) { settle = res; });   /* lock, pre-await */
    const outer = running;

    runOnce()
      .catch(function (e) {
        return { ok: false, changed: false, reason: 'exception', error: String((e && e.message) || e) };
      })
      .then(function (res) {
        lastResult = res;
        running = null;
        if (dirty) {
          dirty = false;
          reconcile().then(settle, function () { settle(res); });
        } else {
          settle(res);
        }
      });

    return outer;
  }

  /* A caller that must see the cart AS IT IS NOW (the checkout gate):
     cancel any pending debounce and guarantee at least one pass that
     starts after this call. */
  function reconcileFresh() {
    if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
    if (running) dirty = true;
    return reconcile();
  }

  function applyFinalSection(data) {
    if (!data) return;
    const sectionHtml = data.sections && data.sections['cart-drawer'];
    if (sectionHtml && window.ILS && typeof window.ILS.morphCartDrawer === 'function') {
      window.ILS.morphCartDrawer(sectionHtml);
    }
    if (window.ILS && typeof window.ILS.updateCartCount === 'function' && typeof data.item_count === 'number') {
      window.ILS.updateCartCount(data.item_count);
    }
    if (window.ILS && typeof window.ILS.updateRewardsFromTotalCents === 'function' && typeof data.total_price === 'number') {
      window.ILS.updateRewardsFromTotalCents(data.total_price);
    }
    /* Broadcast so any other listeners (badges, mini-cart mirrors, etc.)
       that expect Dawn's PUB_SUB_EVENTS.cartUpdate can catch up. Kept
       best-effort — Dawn may not have PUB_SUB_EVENTS loaded on all page
       templates. Our own subscriber ignores source 'ils-gift-logic'. */
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ils-gift-logic', cartData: data });
      }
    } catch (e) {}
    try {
      document.dispatchEvent(new CustomEvent('ils:gift-reconciled', { detail: { itemCount: data.item_count } }));
    } catch (e) {}
  }

  /* -----------------------------------------------------------------
     CORE FIX 5 — event wiring: every path that can change a cart line.
     ----------------------------------------------------------------- */
  let debounceTimer = null;
  function scheduleReconcile(delayMs) {
    if (killed()) return;
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () { debounceTimer = null; reconcile(); }, delayMs || 300);
  }

  function subscribeCartEvents() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === 'ils-gift-logic') return;
        bumpCartVersion('pubsub-cartUpdate');   /* TASK-4850 */
        scheduleReconcile(400);
      });
    }
    /* Dispatched by the drawer (scheduleGiftSync) and the cart page for
       every add / remove / quantity change / variant swap they make. */
    document.addEventListener('cart:update', function () {
      bumpCartVersion('dom-cart-update');   /* TASK-4850 */
      scheduleReconcile(400);
    });

    /* Back / forward restore from the bfcache: the whole JS state is
       frozen and no load event runs, so init()'s own pass never happens —
       yet the cart may have changed in another tab or in the checkout the
       shopper just backed out of. An ordinary (non-persisted) back/forward
       is a real page load and is already covered by init(), so we ignore
       it here rather than schedule the same pass twice. */
    window.addEventListener('pageshow', function (e) {
      if (!e || !e.persisted) return;
      bumpCartVersion('pageshow-persisted');   /* TASK-4850 */
      scheduleReconcile(600);
    });

    /* TASK-4850 — cross-tab safety. Returning to this tab is the one moment an
       out-of-document cart change becomes visible to us, so the token is dropped
       and a fresh validation is forced before any checkout can be reached. */
    if (FASTPATH) {
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState !== 'visible') return;
        bumpCartVersion('visible');
        scheduleReconcile(500);
      });
    }
  }

  /* TASK-4850 — the bounded pre-emptive refresh described at GIFT_TOKEN_TTL_MS.
     Driven both by a real user gesture (so an invalidation is answered promptly)
     and by a slow tick (so a long dwell does not silently let the token lapse).
     Every guard below must pass; the budget makes an unbounded loop impossible. */
  let lastRefreshAt = 0;
  let refreshCount = 0;

  function tokenNeedsRefresh() {
    if (!validated) return true;
    if (validated.version !== cartVersion) return true;
    return (Date.now() - validated.at) > (GIFT_TOKEN_TTL_MS - GIFT_REFRESH_PREEMPT_MS);
  }

  /* `ev` is present for the gesture path. A gesture ON a checkout gate is
     deliberately ignored: that click is about to run reconcileFresh() itself, and
     starting a pass here would only make the gate mark it dirty and force a
     SECOND pass — measured at 356-409 ms instead of one 171 ms read
     (recon/task4850/verify.dev.on.json, D1 under the first build). */
  function maybeRefreshToken(ev) {
    if (!FASTPATH || killed()) return;
    if (running || debounceTimer) return;
    if (!tokenNeedsRefresh()) return;
    if (GIFT_REFRESH_CAPPED && refreshCount >= GIFT_REFRESH_MAX_PER_VIEW) return;
    const now = Date.now();
    if (now - lastRefreshAt < GIFT_REFRESH_MIN_GAP_MS) return;
    try { if (document.visibilityState === 'hidden') return; } catch (e) {}
    try { if (!document.querySelector(GIFT_REFRESH_INTENT_SEL)) return; } catch (e) { return; }
    try {
      const t = ev && ev.target;
      if (t && typeof t.closest === 'function' && t.closest(GIFT_REFRESH_SKIP_SEL)) return;
    } catch (e) {}
    lastRefreshAt = now;
    refreshCount++;
    fastStats.refreshes++;
    reconcile();
  }

  function bindTokenRefresh() {
    if (!FASTPATH) return;
    const h = function (ev) { maybeRefreshToken(ev); };
    ['pointerdown', 'pointerover', 'keydown', 'touchstart', 'scroll'].forEach(function (t) {
      try { document.addEventListener(t, h, { passive: true, capture: true }); }
      catch (e) { try { document.addEventListener(t, h, true); } catch (e2) {} }
    });
    try { setInterval(function () { maybeRefreshToken(null); }, GIFT_REFRESH_TICK_MS); } catch (e) {}
  }

  /* NOT DONE, deliberately — a window blur -> focus invalidator. It would close
     the one case visibilitychange cannot see (a side-by-side WINDOW, where this
     document may never go hidden while the shopper works in the other one), but
     it was built, measured and REMOVED: window focus is also raised by ordinary
     in-page focus moves, and on the real page that invalidated a perfectly valid
     token on the Next click itself — the A-group runs went from 2 ms back to
     351-392 ms (recon/task4850/verify.dev.on.build2.json). The task's stated
     cross-tab requirement is visibilitychange -> visible plus
     pageshow[persisted], which is what is implemented above. The residual
     exposure is bounded by GIFT_TOKEN_TTL_MS, and the file's init pass, its
     pageshow pass and the post-order corrector are unchanged behind it. */

  /* Several live cart writers emit NEITHER of the two events above — the
     delivery upsell's "+ Add", the delivery selector's line-property
     writes, the pre-order popup, cart-notification. Measured on the live
     theme (TASK-4845) and independently in REPORT-4844's control B, where
     a bare fetch add crossed the gift threshold and the reconciler never
     woke. This observer closes that hole with the smallest possible hook:
     it reads the URL, passes the call through untouched, and returns the
     CALLER'S OWN promise — the response is never consumed, cloned or
     delayed, and a rejection handler is attached so we cannot manufacture
     an unhandled rejection. Same shape as custom-add-toast.js's existing
     interceptor. Our own writes carry __ilsGift and are skipped. */
  const CART_WRITE_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  function installCartWriteObserver() {
    const native = window.fetch;
    if (typeof native !== 'function') return;
    window.fetch = function (input, init) {
      let watch = false;
      try {
        if (!(init && init.__ilsGift) && !(input && input.__ilsGift)) {
          let u = '';
          if (typeof input === 'string') u = input;
          else if (input && typeof input.url === 'string') u = input.url;
          watch = !!u && CART_WRITE_RE.test(u);
          /* TASK-4850 CORE CHANGE D — custom-delivery-selector.js's own
             attributes-only / _delivery_*-properties-only writes cannot move the
             merchandise subtotal, so they can never change the gift decision.
             REPORT-4848 §5 measured one wasted /cart.js per such write (five per
             checkout on a 5-line cart) and, worse, each opened a window in which
             a gift update.js could land in the middle of the selector's serial
             property pass. */
          if (watch && FASTPATH && isDeliveryMetaWrite(init)) watch = false;
        }
      } catch (e) {}
      const p = native.apply(this, arguments);
      if (watch) {
        /* TASK-4850: the cart is changing NOW. Invalidate synchronously, before
           the response — a click that lands mid-write must never take the fast
           gate. */
        bumpCartVersion('cart-write');
        try {
          p.then(function (resp) {
            if (resp && (resp.ok || (resp.status >= 200 && resp.status < 300))) scheduleReconcile(450);
          }, function () {});
        } catch (e) {}
      }
      return p;
    };
  }

  /* -----------------------------------------------------------------
     CORE FIX 4 — the checkout gate.

     Both "Checkout" buttons are type="button" gates that custom-cart-
     drawer.js / custom-cart-page.js redirect to /pages/delivery-selector
     (TASK-3340); the selector's own [data-ils-next] is what finally sends
     the shopper to /checkout. All three are real pre-checkout navigations
     and all three are gated here.

     The old guard was non-blocking and fired an add-then-remove pair whose
     second request was created only after the first had resolved — by
     which time the document was being discarded, so only the add landed
     and the duplicate became permanent (REPORT-4842 finding B). That
     design is gone. We now intercept in the CAPTURE phase (ahead of the
     drawer's own bubble-phase gate handler), run the ONE atomic
     reconciliation, wait for it, and only then replay the click so the
     original navigation happens exactly once.
     ----------------------------------------------------------------- */
  /* TASK-4945 — under instant-next [data-ils-next] is NOT in the blocking gate.
     The cart/drawer -> delivery-page gates stay, which is what guarantees the
     delivery page is entered from a reconciled gift state; the delivery page's
     own Next is a pure navigation whose gift correctness is already owned by the
     mutation lifecycle and by the apply-mode post-order corrector. */
  const GATE_SEL_BASE = '#CartDrawer-Checkout, #CartPage-Checkout, [data-ils-checkout-gate]';
  const GATE_SEL = INSTANT_NEXT ? GATE_SEL_BASE : (GATE_SEL_BASE + ', [data-ils-next]');
  /* A gesture ON a gate is ignored (the click is about to reconcile anyway), and
     under instant-next a gesture on Next is ignored too: that click navigates in
     single-digit milliseconds, so a read started on its pointerdown could only
     ever be an abandoned one. Next stays in GIFT_REFRESH_INTENT_SEL, which is a
     PRESENCE test for "a real checkout CTA is on this page", not a target test. */
  const GIFT_REFRESH_SKIP_SEL = INSTANT_NEXT ? (GATE_SEL_BASE + ', [data-ils-next]') : GATE_SEL;

  function showGateError(btn) {
    let msg = '';
    try { msg = (window.cartStrings && window.cartStrings.error) || ''; } catch (e) {}
    if (!msg || /^Translation missing/i.test(msg)) {
      msg = 'There was an error while updating your cart. Please try again.';
    }
    /* Prefer the cart's own error UX where it exists. */
    let host = null;
    try {
      const scope = btn.closest('cart-drawer, .ils-cd, form, body') || document;
      host = scope.querySelector('#CartDrawer-CartErrors, #CartPage-CartErrors, .cart-errors');
    } catch (e) {}
    if (host) { host.textContent = msg; host.hidden = false; return; }
    /* Otherwise a minimal, localized, self-contained note next to the button. */
    try {
      let note = document.getElementById('ILSGiftError');
      if (!note) {
        note = document.createElement('div');
        note.id = 'ILSGiftError';
        note.setAttribute('role', 'alert');
        note.style.cssText = 'margin:8px 0 0;font-size:13px;line-height:1.35;color:#b00020;';
        btn.parentNode.insertBefore(note, btn.nextSibling);
      }
      note.textContent = msg;
    } catch (e) {}
  }

  function clearGateError(btn) {
    try {
      const scope = (btn && btn.closest && btn.closest('cart-drawer, .ils-cd, form, body')) || document;
      scope.querySelectorAll('#CartDrawer-CartErrors, #CartPage-CartErrors, .cart-errors')
        .forEach(function (el) { el.textContent = ''; });
    } catch (e) {}
    try {
      const note = document.getElementById('ILSGiftError');
      if (note && note.parentNode) note.parentNode.removeChild(note);
    } catch (e) {}
  }

  /* The reconciliation morphs the cart drawer, which replaces its inner
     HTML wholesale — the very button that was clicked can be gone by the
     time we are ready to replay. Re-resolve it from the live document. */
  function reResolveGate(btn) {
    try {
      if (btn && btn.isConnected) return btn;
      if (btn && btn.id) {
        const byId = document.getElementById(btn.id);
        if (byId) return byId;
      }
      const all = document.querySelectorAll(GATE_SEL);
      for (let i = 0; i < all.length; i++) {
        if (!all[i].disabled) return all[i];
      }
    } catch (e) {}
    return btn;
  }

  function bindCheckoutGuard() {
    document.addEventListener('click', function (ev) {
      const t = ev.target;
      if (!t || typeof t.closest !== 'function') return;
      const btn = t.closest(GATE_SEL);
      if (!btn) return;

      /* Our own replayed click — let it run the real gate handler. */
      if (btn.__ilsGiftPass) { btn.__ilsGiftPass = false; return; }
      if (killed()) return;

      /* TASK-4850 — THE FAST GATE. When a reconciliation pass has PROVEN this
         exact cart version correct, nothing has been written since, no required
         write has failed and the proof is fresh, this gate has nothing left to
         decide: let the TRUSTED click through untouched. No /cart.js, no
         preventDefault, no stopImmediatePropagation, no replayed synthetic
         click — the page's own handler runs on the real user gesture, which is
         also how REPORT-4848's 166-220 ms floor disappears. Every other case
         falls through to the unchanged blocking reconciliation below. */
      if (giftFastReady()) {
        fastStats.passThroughs++;
        try {
          document.dispatchEvent(new CustomEvent('ils:gift-fastpass', {
            detail: { version: cartVersion, provenAt: validated && validated.at,
                      reason: validated && validated.reason }
          }));
        } catch (e) {}
        return;
      }
      if (FASTPATH) fastStats.gateReconciles++;

      /* Hold the navigation. stopImmediatePropagation keeps the drawer's
         own bubble-phase gate handler from firing behind our back. */
      ev.preventDefault();
      ev.stopPropagation();
      if (typeof ev.stopImmediatePropagation === 'function') ev.stopImmediatePropagation();

      /* Double-submit guard: a second tap while we are reconciling does
         nothing at all. */
      if (btn.__ilsGiftBusy) return;
      btn.__ilsGiftBusy = true;
      try { btn.setAttribute('aria-busy', 'true'); btn.classList.add('is-loading'); } catch (e) {}

      reconcileFresh().then(function (res) {
        const target = reResolveGate(btn);
        btn.__ilsGiftBusy = false;
        try { btn.removeAttribute('aria-busy'); btn.classList.remove('is-loading'); } catch (e) {}
        if (target !== btn) {
          target.__ilsGiftBusy = false;
          try { target.removeAttribute('aria-busy'); target.classList.remove('is-loading'); } catch (e) {}
        }

        /* Only a write we decided on and that FAILED holds the shopper —
           we then know the cart is wrong. A missing config, an unreadable
           cart or an unavailable FX rate mean we made no decision at all;
           stranding the shopper on those would be a worse defect than the
           one being fixed, and the post-order corrector still covers them. */
        if (res && res.blockNavigation) { showGateError(target); return; }

        clearGateError(target);
        target.__ilsGiftPass = true;
        try { target.click(); } catch (e) { target.__ilsGiftPass = false; }
      });
    }, true);
  }

  function init() {
    if (killed()) return;
    installCartWriteObserver();
    subscribeCartEvents();
    bindCheckoutGuard();
    bindTokenRefresh();   /* TASK-4850 — no-op when the kill switch is OFF */
    /* Initial reconciliation on page load — handles a stale persisted cart
       (previous session, thresholds changed, or a wrong gift left by an
       older build of this file). */
    scheduleReconcile(1500);
  }

  /* Minimal, read-mostly surface for the live verification harness and for
     support. `reconcile` is the SAME single-flight reconciler the events
     use — this is not a second writer. */
  window.ILSGift = {
    version: 'TASK-4845',
    reconcile: reconcileFresh,
    readCart: readCart,
    plan: async function () {
      const cfg = await ensureConfig();
      if (!cfg) return { ok: false, reason: 'no-config' };
      const cart = await readCart();
      if (!cart) return { ok: false, reason: 'cart-read-failed' };
      return planGift(cfg, cart);
    },
    lastResult: function () { return lastResult; },
    isRunning: function () { return !!running; },
    /* TASK-5954 — read-only introspection of the ladder this engine will
       actually decide on, and a PURE evaluation of it. Neither touches the
       cart, the network or any DOM: `ladders()` reports the table that is
       loaded (and whether it came from the API or the embedded approved
       snapshot), and `gramsFor()` answers "what would this engine grant for
       N minor units of ISO" without granting anything.

       It exists so a live verification can assert the GRANT RULE on all 48
       approved cells, which is otherwise only reachable by building 48 real
       carts. It is explicitly NOT a substitute for a real cart test — a
       module return is not a gift line — and the task's evidence keeps the
       two apart. */
    ladders: function () {
      if (!cfgCache) return null;
      return {
        source: cfgCache.ladderSource || null,
        version: cfgCache.ladderVersion || null,
        currencies: cfgCache.ladders ? Object.keys(cfgCache.ladders) : [],
        table: cfgCache.ladders || null,
        legacyEurThresholds: cfgCache.thresholds || null,
        giftSku: cfgCache.gift_sku || null,
      };
    },
    gramsFor: function (minor, iso) {
      if (!cfgCache) return null;
      return targetGramsForMinor(cfgCache, minor, iso);
    },
    loadConfig: function () { return ensureConfig(); },
    /* TASK-4850 — the validity token, read-only. custom-delivery-selector.js
       consults fastReady()/cartVersion() to decide whether a Next click can be a
       zero-request one; the verification harness reads fastStats(). `version`
       above deliberately still reads TASK-4845 (REPORT-4845's harness asserts
       it); the fast-path build id is separate. */
    fastPathVersion: 'TASK-4850',
    instantNextVersion: 'TASK-4945',
    fastPath: function () { return !!FASTPATH; },
    instantNext: function () { return !!INSTANT_NEXT; },
    gateSel: function () { return GATE_SEL; },
    fastReady: giftFastReady,
    cartVersion: function () { return cartVersion; },
    validated: function () { return validated ? Object.assign({}, validated) : null; },
    invalidate: function (why) { bumpCartVersion(why || 'manual'); },
    fastStats: function () {
      return {
        enabled: !!FASTPATH, cartVersion: cartVersion,
        validated: validated ? Object.assign({}, validated) : null,
        requiredWriteFailed: requiredWriteFailed, lastInvalidation: lastInvalidation,
        ttlMs: GIFT_TOKEN_TTL_MS, refreshes: refreshCount,
        instantNext: !!INSTANT_NEXT, refreshCapped: !!GIFT_REFRESH_CAPPED,
        refreshMinGapMs: GIFT_REFRESH_MIN_GAP_MS, gateSel: GATE_SEL,
        bumps: fastStats.bumps, arms: fastStats.arms,
        passThroughs: fastStats.passThroughs, gateReconciles: fastStats.gateReconciles,
        ready: giftFastReady()
      };
    },
  };

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
